/**
 * The owner's approval queue — the one rendering of `change_requests`, shared by the Approves page (/approves) and
 * the "Waiting on your OK" block at the top of Needs Attention (owner, Nov 2 2026: "approves and needs attention
 * together"). A row is a manager's change, or one step of a Claude one-off (a "Read first —" note, a move, a text,
 * a bid edit); approving replays it verbatim as the owner (services/changeRequests), rejecting drops it.
 *
 * The queue is owner-only on the server (requireOwner → 403 for everyone else), so the hook takes `enabled`: the
 * page asks regardless and explains a 403; the block asks only when the login is the owner.
 */
import React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Check, X, Loader } from 'lucide-react';
import { api } from '../services/api';

export type ChangeRequest = {
  id: number;
  method: string;
  path: string;
  body: string | null;
  summary: string | null;
  requested_by_name: string | null;
  requested_at: string;
};

export const when = (ts: string) => {
  const d = new Date(ts);
  if (isNaN(d.getTime())) return '';
  const mins = Math.round((Date.now() - d.getTime()) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  if (mins < 60 * 24) return `${Math.round(mins / 60)}h ago`;
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

/** A one-off's "Read first —" note (server/scripts/oneoff): what the batch below does. It reads before any step. */
export const isReadFirst = (r: ChangeRequest) => /^\s*read first\b/i.test(r.summary || '');

/**
 * The server lists newest batch first, and steps inside one batch in order (one requested_at, id ASC). A "Read
 * first —" note is already the first row of its own batch; this keeps every note above every step, so two batches
 * never read step, step, note, step.
 */
export function orderForReading(rows: ChangeRequest[]): ChangeRequest[] {
  return [...rows.filter(isReadFirst), ...rows.filter(r => !isReadFirst(r))];
}

/** Say WHICH thing went wrong — a 403 is the owner-only rule working, not a fault (audit of Sep 21 2026). */
export function whyNotLoaded(error: any): { title: string; body: string } {
  const status = error?.response?.status;
  const said = error?.response?.data?.message;
  if (status === 403) return { title: 'This queue is the owner’s', body: `You are signed in without owner access${said ? ` — ${said}` : ''}. Switch to your Admin hat and it will load.` };
  if (status === 401) return { title: 'Signed out', body: 'Your session has expired. Sign in again and come back.' };
  if (status >= 500) return { title: 'The server could not read it', body: `${said || 'It answered with an error.'} (${status})` };
  if (status) return { title: 'That did not load', body: `${said || 'The server refused it.'} (${status})` };
  return { title: 'Could not reach the app', body: 'No answer at all — check the connection, or the app may be mid-deploy. It will retry on its own.' };
}

export function useApprovalsQueue(enabled = true) {
  const qc = useQueryClient();
  const query = useQuery('change-requests',
    () => api.get('/change-requests').then(r => r.data as { requests: ChangeRequest[]; count: number }),
    { enabled, refetchInterval: 60_000, retry: false });

  // An approved change is a real admin action somewhere in the app — the lists that commonly carry it are refreshed,
  // and Needs Attention with them, since the block lives there.
  const done = (msg: string) => {
    toast.success(msg);
    ['change-requests', 'attention', 'my-day-attention', 'customers', 'jobs', 'routes'].forEach(k => qc.invalidateQueries(k));
  };
  const approve = useMutation((id: number) => api.post(`/change-requests/${id}/approve`).then(r => r.data), {
    onSuccess: (d: any) => done(d?.message || 'Approved'),
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not approve that'); qc.invalidateQueries('change-requests'); },
  });
  const reject = useMutation((id: number) => api.post(`/change-requests/${id}/reject`).then(r => r.data), {
    onSuccess: (d: any) => done(d?.message || 'Rejected'),
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not reject that'); qc.invalidateQueries('change-requests'); },
  });
  const approveAll = useMutation(() => api.post('/change-requests/approve-all').then(r => r.data), {
    // Whole batches touch anything at all: refresh every query, not a guessed few.
    onSuccess: (d: any) => { toast.success(d?.message || 'Approved'); qc.invalidateQueries(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not approve them'); qc.invalidateQueries('change-requests'); },
  });

  const requests = orderForReading(query.data?.requests || []);
  const busy = approve.isLoading || reject.isLoading || approveAll.isLoading;
  return { ...query, requests, approve, reject, approveAll, busy };
}

type Queue = ReturnType<typeof useApprovalsQueue>;

/** One "Approve all N" button — the page's header and the block's header both carry it when there is more than one. */
export function ApproveAllButton({ queue, className = 'btn-primary text-sm disabled:opacity-50' }: { queue: Queue; className?: string }) {
  const n = queue.requests.length;
  if (n < 2) return null;
  return (
    <button onClick={() => queue.approveAll.mutate()} disabled={queue.busy} className={className}>
      {queue.approveAll.isLoading ? 'Approving…' : `Approve all ${n}`}
    </button>
  );
}

/** The rows, exactly as the Approves page has always drawn them: the sentence, who and when, the exact call folded away, No / Approve. */
export function ApprovalRows({ queue }: { queue: Queue }) {
  const { requests, approve, reject, busy } = queue;
  return (
    <div className="space-y-2.5">
      {requests.map(r => (
        <div key={r.id} className={`rounded-2xl border bg-white px-4 py-3.5 ${isReadFirst(r) ? 'border-violet-300' : 'border-gray-200'}`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <p className="text-[15px] font-semibold text-gray-900">{r.summary || `${r.method} ${r.path}`}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {r.requested_by_name || 'A manager'} · {when(r.requested_at)}
              </p>
              {/* What it would actually call, for when the sentence is not enough. */}
              <details className="mt-1.5">
                <summary className="text-[11px] text-gray-500 cursor-pointer">The exact change</summary>
                <p className="mt-1 text-[11px] font-mono text-gray-600 break-all">{r.method} {r.path}</p>
                {r.body && <pre className="mt-1 text-[11px] text-gray-600 bg-gray-50 border border-gray-100 rounded-lg p-2 overflow-x-auto">{(() => { try { return JSON.stringify(JSON.parse(r.body), null, 2); } catch { return r.body; } })()}</pre>}
              </details>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button onClick={() => reject.mutate(r.id)} disabled={busy}
                className="px-3 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-600 inline-flex items-center gap-1 disabled:opacity-50">
                <X className="h-4 w-4" /> No
              </button>
              <button onClick={() => approve.mutate(r.id)} disabled={busy}
                className="px-3.5 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold inline-flex items-center gap-1 disabled:opacity-50">
                {approve.isLoading ? <Loader className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Approve
              </button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
